<!--

============================================================================================================
||    PageFilters component                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
Component adds standardized filter elements to a page.

|   Features
------------------------------------------------------------------------------------------------------------
  - Search input
  - "New" button - this only emits an event to the parent component to open a modal or a new page.
  - Filter button - this should be customizable... TODO edit this description once this is finalized.

|   Props
------------------------------------------------------------------------------------------------------------
  - show-search           : Boolean    : Show search input.
  - search-text           : String     : Placeholder text for the search input.
  - search-debounce-time  : Number     : Time in milliseconds for the debounce function.
  - show-new              : Boolean    : Show "New" button.
  - no-debounce           : Boolean    : Disable debounce for search input.

|   Events
------------------------------------------------------------------------------------------------------------
  - add-new - When user clicks on the "New" button.
  - filter - When user edits a filter.
  - search - When user searches for something.

|   Slots
------------------------------------------------------------------------------------------------------------
  - right - To add custom button to the right.
  - prefix - To add custom content before the item icon and text.
  - suffix - To add custom content after the item text.

|   TODO
------------------------------------------------------------------------------------------------------------
  - "New" button
  - filter button

-->

<template>
  <div
    ref="tableTools"
    class="table-tools d-flex align-items-center justify-content-between">
    <div class="flex flex-align-center w-100">
      <div
        ref="leftToolsRef"
        class="tools-left d-flex align-items-center greedy no-shrink">
        <SearchInput
          v-if="showSearch"
          ref="searchInput"
          v-model="searchQuery"
          :placeholder="searchText"
          :no-debounce="noDebounce"
          @input="searchIt"
          @change="searchIt" />
        <FilterSingle
          v-if="filterNumbers"
          v-model="numbersFilter"
          :items="numbersToFilter"
          track-by="id"
          label="name"
          sub-label="phone"
          track-icon="icon"
          group-title="Numbers"
          placement="left"
          :show-prefix-icon="false"
          :show-prefix-tick="false" />
      </div>
      <div class="tools-right no-shrink flex flex-align-center">
        <b-button
          v-if="!hideNew"
          id="new-btn"
          class="sb-btn rainbow-background flex flex-align-center mr-1"
          @click="emitNew">
          <SBIcon
            class="mr-2 rainbow-text"
            :icon="SBIconList.PLUS"
            :color="SBColors.blackPrimary"
            :size="8"/>
          <p class="rainbow-text">{{ newButtonText }}</p>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { debounce } from 'debounce';
import SearchInput from '@/components/business/widgets/SearchInput';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import FilterSingle from '@/components/common/FilterSingle';

export default {
  name: 'PageFilters',
  components: {
    SearchInput,
    SBIcon,
    FilterSingle,
  },
  props: {
    showSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    searchText: {
      type: String,
      required: false,
      default: '',
    },
    searchDebounceTime: {
      type: Number,
      required: false,
      default: 500,
    },
    newButtonText: {
      type: String,
      required: false,
      default: 'New',
    },
    noDebounce: {
      type: Boolean,
      required: false,
      default: false,
    },
    filterNumbers: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideNew: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      changeTimeout: null,
      SBColors,
      SBIconList,
      numbersFilter: [],
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisationBusinesses: 'organisation/currentOrganisationBusinesses',
      numberIntegrationMap: 'organisation/numberIntegrationMap',
    }),
    numbersToFilter() {
      const businessesWithEmoji = this.currentOrganisationBusinesses.map((number, index) => {
        const item = {
          id: number.id,
          name: number.name,
          phone: number.phone,
          icon: number.emoji,
        };
        // We decided not to use integration icons for now - 02.07.2024
        // if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length === 1) {
        //   delete item.icon;
        //   item.integration = this.numberIntegrationMap[number.id][0];
        // } else if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length) {
        //   delete item.icon;
        //   item.integrationCount = this.numberIntegrationMap[number.id].length;
        // }
        return item;
      });
      return businessesWithEmoji;
    },
  },
  watch: {
    numbersFilter: {
      handler() {
        this.modelChange();
      },
      deep: true,
    },
  },
  methods: {
    searchIt() {
      clearTimeout(this.changeTimeout);
      this.changeTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery);
        this.modelChange();
      }, this.noDebounce ? 0 : this.searchDebounceTime);
    },
    emitNew() {
      this.$emit('add-new');
    },
    modelChange() {
      const filters = {
        search: this.searchQuery,
        numbersFilter: this.numbersFilter,
      };
      this.$emit('input', filters);
    },
  },
};
</script>

<style lang="scss" scoped>
.table-tools {
  margin-top: 15px;
  background: $white;
  padding: 20px 0;
  border-bottom: solid 1px #e6ebed;
}
</style>
